/**
 * 推广中心（v3.39）—— 借鉴《Web3三方共赢消费奖励平台》推广体系：
 * 推广者分级 V1-V5（流水累计自动晋升）/ 直推+二级分润 / 团队级差 / 锁客永久绑定 /
 * 商圈分红（持卡按贡献分池领取）/ 每日任务中心 / 回购销毁通缩 / 商家信誉分。
 * 全部数据来自链端 meshStats（/api/v1/meshlink），金额口径为链上 DPB 数值。
 */

import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { formatInt } from '@/lib/format';
import type { Address } from '@shared/chain';

interface PromoStats {
  promoter_ranks?: Record<string, { level: number; volume: number; direct_count?: number; updated_height?: number }>;
  promoter_tree?: Record<string, { referrer?: string; height?: number }>;
  customer_merchants?: Record<string, Record<string, { spend_total?: number; count?: number; first_ts?: number }>>;
  district_cards?: Record<string, { district?: string; activated_height?: number }>;
  district_pools?: Record<string, { count?: Record<string, number>; entries?: Record<string, number>; pool?: number; issued?: number }>;
  tasks?: Record<string, Record<string, { kinds?: string[]; rewarded?: number; rewarded_ts?: number }>>;
  merchant_scores?: Record<string, { score?: number; updated?: number }>;
  buybacks?: { count?: number; total?: number };
  params?: Record<string, number>;
  redemptions?: Array<{
    promo_id: string;
    from: string;
    amount: number;
    promoter?: string;
    direct_pay?: number;
    second_pay?: number;
    diff_pay?: number;
    promoter_level?: number;
    district?: string;
    score_bonus?: number;
    lock_bonus?: number;
    buyback?: number;
    height?: number;
    ts?: number;
  }>;
}

const LEVEL_LABEL = ['', 'V1 新星', 'V2 银推', 'V3 金推', 'V4 铂推', 'V5 钻石推'];

function fmt(v: number | undefined): string {
  return Number(v ?? 0).toLocaleString('zh-CN', { maximumFractionDigits: 4 });
}

export function PromoCenterBoard({ compact = false }: { compact?: boolean }): JSX.Element {
  const address = useIdentityStore((s) => s.address) as Address | undefined;
  const [stats, setStats] = useState<PromoStats>({});
  const [busy, setBusy] = useState(false);
  const [refAddr, setRefAddr] = useState('');

  const refresh = useCallback(async (): Promise<void> => {
    if (!address) return;
    setBusy(true);
    try {
      const [st, pl] = await Promise.all([
        container.chain.meshStats().catch(() => ({}) as PromoStats),
        container.chain.promoList().catch(() => ({ items: [], redemptions: [] }) as Record<string, unknown>),
      ]);
      const merged: PromoStats = {
        ...st,
        redemptions: (pl?.redemptions as PromoStats['redemptions']) ?? [],
      };
      setStats(merged);
    } finally {
      setBusy(false);
    }
  }, [address]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const params = stats.params ?? {};
  const ranks = stats.promoter_ranks ?? {};
  const tree = stats.promoter_tree ?? {};
  const myRank = address ? ranks[address.toLowerCase()] : undefined;
  const myRef = address ? tree[address.toLowerCase()]?.referrer : undefined;
  const thresholds = [
    Number(params.rank_vol_v2 ?? 100),
    Number(params.rank_vol_v3 ?? 300),
    Number(params.rank_vol_v4 ?? 1000),
    Number(params.rank_vol_v5 ?? 3000),
  ];
  const level = myRank?.level ?? 1;
  const volume = myRank?.volume ?? 0;
  const nextTh = level >= 5 ? null : thresholds[level - 1];
  const progress = nextTh ? Math.min(100, Math.round((volume / nextTh) * 100)) : 100;
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const myTasks = address ? (stats.tasks?.[address.toLowerCase()] ?? {}) : {};
  const todayTask = myTasks[todayKey];
  const taskActions = Math.max(1, Math.floor(Number(params.task_actions ?? 3)));
  const doneKinds = new Set(todayTask?.kinds ?? []).size;
  const myLocks = address ? (stats.customer_merchants?.[address.toLowerCase()] ?? {}) : {};
  const myCard = address ? stats.district_cards?.[address.toLowerCase()] : undefined;
  const myDist = myCard?.district;
  const myPool = myDist ? stats.district_pools?.[myDist] : undefined;
  const myEntries = (myPool?.entries ?? {})[address?.toLowerCase() ?? ''] ?? 0;
  const myScore = address ? (stats.merchant_scores?.[address.toLowerCase()]?.score ?? 0) : 0;
  const bb = stats.buybacks;

  const bindUp = async (): Promise<void> => {
    if (!address || !refAddr.trim()) return;
    setBusy(true);
    try {
      await container.chain.promoRank({
        action: 'bind',
        from: address,
        referrer: refAddr.trim() as Address,
        signature: container.identity.signObject({ action: 'promo_rank', from: address, referrer: refAddr.trim(), ts: Date.now() }),
      });
      useUiStore.getState().pushToast('success', '上级绑定成功（推广层级 ≤2 级）');
      setRefAddr('');
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `绑定失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const claimDistrict = async (): Promise<void> => {
    if (!address || !myDist) return;
    setBusy(true);
    try {
      await container.chain.districtClaim({
        action: 'claim',
        from: address,
        district: myDist,
        signature: container.identity.signObject({ action: 'district_claim', from: address, district: myDist, ts: Date.now() }),
      });
      useUiStore.getState().pushToast('success', `商圈分红已领取（${fmt(myEntries)} DPB）`);
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `领取失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const claimRank = async (): Promise<void> => {
    if (!address) return;
    setBusy(true);
    try {
      await container.chain.promoRank({
        action: 'claim',
        from: address,
        signature: container.identity.signObject({ action: 'promo_rank', from: address, ts: Date.now() }),
      });
      useUiStore.getState().pushToast('success', '等级申报完成');
      void refresh();
    } catch (err) {
      useUiStore.getState().pushToast('error', `申报失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const myReds = (stats.redemptions ?? []).filter((r) => {
    const me = address?.toLowerCase();
    return me && r.promoter?.toLowerCase() === me;
  });
  const directSum = myReds.reduce((s, r) => s + Number(r.direct_pay ?? 0), 0);
  const secondSum = myReds.reduce((s, r) => s + Number(r.second_pay ?? 0), 0);
  const diffSum = myReds.reduce((s, r) => s + Number(r.diff_pay ?? 0), 0);

  if (!address) return <EmptyState title="请先创建身份" description="登录后查看推广等级、任务与商圈分红" />;

  return (
    <div className="space-y-3">
      {/* 推广等级 */}
      <section className="card p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-sm font-medium text-text">我的推广等级</h2>
          <Badge tone="ok">{LEVEL_LABEL[Math.max(1, Math.min(5, level))]}</Badge>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          核销流水累计自动晋升：V2 {fmt(thresholds[0])} / V3 {fmt(thresholds[1])} / V4 {fmt(thresholds[2])} / V5 {fmt(thresholds[3])} DPB。
          直推拿推广佣金的 80%，上级拿二级 20% + 团队级差（每级差 +{Number(params.rank_diff_bps ?? 500) / 100}%）。
        </p>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-dim">当前累计核销流水</span>
          <span className="font-semibold text-text">{fmt(volume)} DPB</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-panel-2">
          <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1 text-[11px] text-dim">
          {nextTh ? `距 ${LEVEL_LABEL[level + 1]} 还需 ${fmt(Math.max(0, nextTh - volume))} DPB` : '已达最高等级 V5 钻石推'}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" loading={busy} disabled={!myRef} onClick={() => void claimRank()}>
            申报当前等级
          </Button>
          {!myRef && (
            <div className="flex flex-1 items-center gap-2 rounded-btn bg-panel-2 px-3 py-2 text-[11px] text-dim sm:flex-none">
              绑定上级：
              <input
                className="flex-1 rounded-btn border border-line bg-surface px-2 py-1.5 text-xs text-text outline-none sm:w-64"
                placeholder="0x…上级地址"
                value={refAddr}
                onChange={(e) => setRefAddr(e.target.value)}
              />
              <Button variant="outline" loading={busy} disabled={!refAddr.trim()} onClick={() => void bindUp()}>
                绑定
              </Button>
            </div>
          )}
          {myRef && <p className="text-[11px] text-dim">已绑定上级：{myRef.slice(0, 10)}…（推广层级 ≤2 级，合规护栏）</p>}
        </div>
      </section>

      {/* 推广收益明细 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">推广收益明细</h2>
        {myReds.length === 0 ? (
          <p className="text-xs text-dim">暂无推广收益——把你的核销链接（推广码）发给朋友，朋友核销即计直推佣金。</p>
        ) : (
          <>
            <div className="mb-2 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-btn bg-panel-2 px-2 py-2">
                <div className="text-base font-semibold text-text">{fmt(directSum)}</div>
                <div className="text-[10px] text-dim">直推佣金 (80%)</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-2 py-2">
                <div className="text-base font-semibold text-text">{fmt(secondSum)}</div>
                <div className="text-[10px] text-dim">二级佣金 (20%)</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-2 py-2">
                <div className="text-base font-semibold text-text">{fmt(diffSum)}</div>
                <div className="text-[10px] text-dim">团队级差</div>
              </div>
            </div>
            {!compact && (
              <ul className="max-h-56 space-y-1.5 overflow-auto text-[11px] text-dim">
                {myReds.slice(-20).reverse().map((r, i) => (
                  <li key={`${r.promo_id}-${i}`} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-1.5">
                    <span className="truncate">核销 {fmt(r.amount)} DPB · 等级 V{r.promoter_level ?? 1}{r.district ? ` · 商圈 ${r.district.slice(0, 6)}…` : ''}</span>
                    <span className="font-medium text-text">+{fmt(Number(r.direct_pay ?? 0) + Number(r.second_pay ?? 0) + Number(r.diff_pay ?? 0))}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {/* 每日任务 */}
      <section className="card p-5">
        <h2 className="mb-1 text-sm font-medium text-text">每日任务中心</h2>
        <p className="mb-3 text-xs leading-relaxed text-dim">
          每日完成浏览 / 搜索 / 签到 ≥{taskActions} 类行为，自动领取 {fmt(Number(params.task_bonus_dpb ?? 0.01))} DPB（金库出，每日一次）。
        </p>
        <div className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs">
          <span className="text-dim">今日进度</span>
          <span className="font-semibold text-text">{doneKinds}/{taskActions} 类行为</span>
        </div>
        {todayTask?.rewarded === 1 && <p className="mt-2 text-[11px] text-ok">今日任务奖励已领取</p>}
      </section>

      {/* 商圈分红 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">商圈分红（持卡按贡献分池）</h2>
        {!myDist ? (
          <p className="text-xs text-dim">
            在同一商圈累计核销 ≥{Math.max(1, Math.floor(Number(params.district_card_threshold ?? 3)))} 次自动获得商圈卡。
            商圈池含商家让利注入（{Number(params.district_bonus_bps ?? 500) / 100}%）+ 金库补贴 5%，按贡献权重分红。
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-btn bg-panel-2 px-2 py-2">
                <div className="text-base font-semibold text-text">{myDist.slice(0, 8)}…</div>
                <div className="text-[10px] text-dim">持卡商圈</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-2 py-2">
                <div className="text-base font-semibold text-text">{fmt(myEntries)}</div>
                <div className="text-[10px] text-dim">我的贡献权重</div>
              </div>
              <div className="rounded-btn bg-panel-2 px-2 py-2">
                <div className="text-base font-semibold text-text">{fmt(myPool?.pool ?? 0)}</div>
                <div className="text-[10px] text-dim">商圈池总额</div>
              </div>
            </div>
            <Button className="mt-3" loading={busy} disabled={myEntries <= 0} onClick={() => void claimDistrict()}>
              领取商圈分红（{fmt(myEntries)} DPB）
            </Button>
          </>
        )}
      </section>

      {/* 锁客 + 信誉 + 通缩 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">锁客 / 信誉 / 通缩</h2>
        <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-3">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{Object.keys(myLocks).length}</div>
            <div className="text-[11px] text-dim">我锁定的商家</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{myScore.toFixed(1)}</div>
            <div className="text-[11px] text-dim">我的商家信誉分</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{formatInt(bb?.count ?? 0)} 笔 / {fmt(bb?.total)}</div>
            <div className="text-[11px] text-dim">平台回购销毁</div>
          </div>
        </div>
        {!compact && Object.keys(myLocks).length > 0 && (
          <ul className="mt-2 space-y-1 text-[11px] text-dim">
            {Object.entries(myLocks).slice(0, 5).map(([m, v]) => (
              <li key={m} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-1.5">
                <span className="truncate">{m.slice(0, 10)}…</span>
                <span>累计消费 {fmt(v?.spend_total ?? 0)} DPB · {formatInt(v?.count ?? 0)} 次</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export function PromoCenterPage(): JSX.Element {
  return (
    <div className="mx-auto max-w-xl px-4 py-5">
      <div className="mb-4">
        <h1 className="text-base font-semibold text-text">推广中心</h1>
        <p className="text-xs text-dim">推广者分级 · 直推/二级分润 · 商圈分红 · 任务中心 · 锁客绑定 · 回购通缩（v3.39）</p>
      </div>
      <PromoCenterBoard />
    </div>
  );
}
